/**
 * Simple logger utility for frontend debugging with file export
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
} as const;

type LogLevel = keyof typeof LOG_LEVELS;

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  prefix: string;
  message: string;
  data?: any;
}

// In-memory log buffer (last 1000 entries)
const LOG_BUFFER: LogEntry[] = [];
const MAX_BUFFER_SIZE = 1000;
const LOG_STORAGE_KEY = 'vibe_kanban_logs';
const LOG_DATE_KEY = 'vibe_kanban_log_date';

// Save logs to localStorage periodically
function saveLogsToStorage() {
  try {
    const today = new Date().toISOString().split('T')[0];
    const storedDate = localStorage.getItem(LOG_DATE_KEY);

    // If date changed, clear old logs (daily rotation)
    if (storedDate !== today) {
      localStorage.removeItem(LOG_STORAGE_KEY);
      localStorage.setItem(LOG_DATE_KEY, today);
    }

    // Get existing logs from storage
    const existingLogs = localStorage.getItem(LOG_STORAGE_KEY);
    const logs = existingLogs ? JSON.parse(existingLogs) : [];

    // Append new logs (keep last 5000 entries)
    const combinedLogs = [...logs, ...LOG_BUFFER].slice(-5000);
    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(combinedLogs));

    // Clear buffer
    LOG_BUFFER.length = 0;
  } catch (error) {
    console.error('Failed to save logs to storage:', error);
  }
}

// Auto-save logs every 30 seconds
setInterval(saveLogsToStorage, 30000);

// Save logs before page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', saveLogsToStorage);
}

class Logger {
  private level: LogLevel;
  private prefix: string;

  constructor(prefix: string = '[App]', level: LogLevel = 'INFO') {
    this.prefix = prefix;
    this.level = level;

    // In development, default to DEBUG
    if (import.meta.env.DEV) {
      this.level = 'DEBUG';
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.level];
  }

  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString();
    return `${timestamp} ${this.prefix} [${level}] ${message}`;
  }

  private addToBuffer(level: LogLevel, message: string, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      prefix: this.prefix,
      message,
      data,
    };

    LOG_BUFFER.push(entry);

    // Limit buffer size
    if (LOG_BUFFER.length > MAX_BUFFER_SIZE) {
      LOG_BUFFER.shift();
    }
  }

  debug(message: string, data?: any) {
    if (this.shouldLog('DEBUG')) {
      console.debug(this.formatMessage('DEBUG', message), data || '');
      this.addToBuffer('DEBUG', message, data);
    }
  }

  info(message: string, data?: any) {
    if (this.shouldLog('INFO')) {
      console.info(this.formatMessage('INFO', message), data || '');
      this.addToBuffer('INFO', message, data);
    }
  }

  warn(message: string, data?: any) {
    if (this.shouldLog('WARN')) {
      console.warn(this.formatMessage('WARN', message), data || '');
      this.addToBuffer('WARN', message, data);
    }
  }

  error(message: string, error?: any) {
    if (this.shouldLog('ERROR')) {
      console.error(this.formatMessage('ERROR', message), error || '');
      this.addToBuffer('ERROR', message, error);

      // Also log stack trace if available
      if (error?.stack) {
        console.error('Stack trace:', error.stack);
      }
    }
  }

  group(label: string) {
    console.group(`${this.prefix} ${label}`);
  }

  groupEnd() {
    console.groupEnd();
  }
}

/**
 * Export logs as a downloadable file
 */
export function exportLogs() {
  try {
    // Flush current buffer
    saveLogsToStorage();

    // Get logs from storage
    const storedLogs = localStorage.getItem(LOG_STORAGE_KEY);
    const logs = storedLogs ? JSON.parse(storedLogs) : [];

    // Format logs as text
    const logText = logs
      .map((entry: LogEntry) => {
        const dataStr = entry.data ? ` ${JSON.stringify(entry.data)}` : '';
        return `${entry.timestamp} ${entry.prefix} [${entry.level}] ${entry.message}${dataStr}`;
      })
      .join('\n');

    // Create timestamp for filename: YYYY-MM-DD-HHMMSS
    const now = new Date();
    const timestamp = now
      .toISOString()
      .replace(/:/g, '')
      .replace(/\..+/, '')
      .replace('T', '-')
      .slice(0, 15);

    // Create download
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${timestamp}.frontend.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.info('Logs exported successfully');
  } catch (error) {
    console.error('Failed to export logs:', error);
  }
}

/**
 * Clear all stored logs
 */
export function clearLogs() {
  try {
    localStorage.removeItem(LOG_STORAGE_KEY);
    localStorage.removeItem(LOG_DATE_KEY);
    LOG_BUFFER.length = 0;
    console.info('Logs cleared');
  } catch (error) {
    console.error('Failed to clear logs:', error);
  }
}

// Create default logger instances
export const logger = new Logger('[App]');
export const apiLogger = new Logger('[API]');
export const wsLogger = new Logger('[WebSocket]');
export const authLogger = new Logger('[Auth]');

// Export Logger class for custom loggers
export { Logger };
